import { ArrowRight } from 'lucide-react';
import posthog from 'posthog-js';
import { useTranslation } from 'react-i18next';
import { generatePath, Link, useNavigate } from 'react-router';
import scrollIntoView from 'smooth-scroll-into-view-if-needed';

import Badge from '@/components/common/Badge';
import OnesuranceIcon from '@/components/icons/Onesurance';
import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import { Button } from '@/components/shadcn/button';
import useSelectedParty from '@/features/analysis/hooks/useSelectedParty';
import paths from '@/lib/paths';
import hasItems from '@/lib/utils/hasItems';

import useOnesuranceRecommendations from '../../queries/hooks/useOnesuranceRecommendation';
import { getBadgeColor } from '../../utils';

export default function OnesuranceBadges() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    analysis: { email, analysis, isAnalyzing },
  } = useEmail();
  const party = useSelectedParty();

  const isAnalysisForSelectedParty =
    Boolean(party?.id) && analysis?.party_id === party?.id;

  const { data } = useOnesuranceRecommendations(email?.id ?? '', {
    enabled: Boolean(email?.id) && isAnalysisForSelectedParty,
  });

  const { recommendations } = data ?? {};

  const handleClick = (type: 'CHURN' | 'SALES' | 'CARE') => () => {
    posthog.capture('Onesurance badge clicked', {
      type,
      messageId: email?.message_id,
      partyId: party?.id,
    });

    navigate(generatePath(paths.onesurance.recommendations));

    setTimeout(() => {
      const elementId = `onesurance-${type.toLowerCase()}`;
      const element = document.getElementById(elementId);
      if (!element) return;

      scrollIntoView(element, {
        behavior: 'smooth',
        duration: 500,
        block: 'start',
      });
    }, 10);
  };

  const handleRecommendationsLinkClick = () => {
    posthog.capture('Onesurance recommendations link clicked', {
      messageId: email?.message_id,
      partyId: party?.id,
    });
  };

  if (isAnalyzing || !isAnalysisForSelectedParty) return null;

  const relevantRecommendations = recommendations?.filter(
    (recommendation) => recommendation.type !== 'CARE',
  );

  if (!hasItems(relevantRecommendations)) {
    return (
      <div className="text-muted-foreground inline-flex cursor-not-allowed items-center justify-center gap-1 self-start rounded-md bg-neutral-100 px-2 py-1 text-xs dark:bg-neutral-800">
        <OnesuranceIcon className="size-3" />

        {t('ONESURANCE_RECOMMENDATIONS_NONE')}
      </div>
    );
  }

  const churnBadge = relevantRecommendations.find(
    (recommendations) =>
      recommendations.type === 'CHURN' && recommendations.level === 'HIGH',
  );
  const salesBadge = relevantRecommendations.find(
    (recommendations) =>
      recommendations.type === 'SALES' && recommendations.level === 'HIGH',
  );

  if (!churnBadge && !salesBadge) {
    return (
      <div className="flex">
        <Button
          size="xs"
          variant="ghost"
          className="bg-neutral-100 dark:bg-neutral-800"
          asChild
        >
          <Link
            to={generatePath(paths.onesurance.recommendations)}
            onClick={handleRecommendationsLinkClick}
          >
            <OnesuranceIcon className="size-3" />

            {t('ONESURANCE_RECOMMENDATIONS_LINK')}

            <ArrowRight />
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1">
      {churnBadge && (
        <Badge
          key={churnBadge.type}
          color={getBadgeColor(churnBadge.type, churnBadge.level)}
          onClick={handleClick(churnBadge.type)}
        >
          <OnesuranceIcon className="size-3" title="Powered by Onesurance" />

          {t(`ONESURANCE_BADGE_${churnBadge.type}_HIGH`)}
        </Badge>
      )}

      {salesBadge && (
        <Badge
          key={salesBadge.type}
          color={getBadgeColor(salesBadge.type, salesBadge.level)}
          onClick={handleClick(salesBadge.type)}
        >
          <OnesuranceIcon className="size-3" title="Powered by Onesurance" />

          {t(`ONESURANCE_BADGE_${salesBadge.type}_HIGH`)}
        </Badge>
      )}
    </div>
  );
}
